import React, { useEffect, useState } from "react";
import { AwardBonusOfferRequest, BonusStatus, DepositRewardConfig } from "../../http/protocol";
import { getContainer } from "../../ioc/IOCSetup";
import { Collapsible } from "../Collapsible";
import { t } from "../../i18n";

const PERCENTAGE_MULTIPLIER = 100;

interface Props {
  userId: number;
}

export function UserDepositRewardComponent({ userId }: Props) {
  const [configs, setConfigs] = useState<DepositRewardConfig[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<number | undefined>(undefined);
  const [expiryInDays, setExpiryInDays] = useState<number | string>("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setLoading(true);
    getContainer()
      .getDepositRewardConfigService()
      .list({ limit: 500, offset: 0, status: BonusStatus.ENABLED })
      .then((resp) => {
        const items = (resp.items || []).filter((c) => c.status === "ENABLED");
        setConfigs(items);
        setSelectedConfigId(items[0]?.id);
      })
      .finally(() => setLoading(false));
  }, []);

  const selectedConfig = configs.find((c) => c.id === selectedConfigId);

  const disabled = !selectedConfigId || !expiryInDays || Number(expiryInDays) <= 0 || submitting;

  function handleSubmit() {
    if (disabled) return;
    setSubmitting(true);
    setError(undefined);
    setSuccess(false);

    const request: AwardBonusOfferRequest = {
      depositRewardConfigId: selectedConfigId,
      claimableOnNextDeposit: true,
      expiryInDays: Number(expiryInDays),
      userId,
    };

    getContainer()
      .getBonusOfferService()
      .awardUserBonus(request)
      .then(() => {
        setSuccess(true);
        setExpiryInDays("");
      })
      .catch((err: any) => {
        setError(err?.message || "An error occurred");
      })
      .finally(() => setSubmitting(false));
  }

  return (
    <Collapsible
      title={t("title.manualDepositReward")}
      collapsed={true}
    >
      <div className="manual-adjustment-form container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <select
              className="form-control"
              value={selectedConfigId ?? ""}
              onChange={(e) => {
                setSelectedConfigId(Number(e.target.value));
                setSuccess(false);
                setError(undefined);
              }}
              disabled={loading || configs.length === 0}
            >
              {configs.map((c) => (
                <option
                  key={c.id}
                  value={c.id}
                >
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedConfig?.depositPercentage != null && (
          <div className="row">
            <div className="col-sm-12">
              <p className="help-block">
                {selectedConfig.depositPercentage * PERCENTAGE_MULTIPLIER}% of deposit, capped at {selectedConfig.maxRewardAmount} {selectedConfig.currencyCode}
              </p>
            </div>
          </div>
        )}

        {selectedConfig && selectedConfig.depositPercentage == null && (
          <div className="row">
            <div className="col-sm-12">
              <p className="help-block">{selectedConfig.rewardType}</p>
            </div>
          </div>
        )}

        <div className="row mt-3">
          <div className="col-sm-6">
            <label>Expiry in days</label>
            <input
              type="number"
              className="form-control"
              value={expiryInDays}
              min={1}
              onChange={(e) => {
                setExpiryInDays(e.target.value);
                setSuccess(false);
                setError(undefined);
              }}
            />
          </div>
          <div className="col-sm-6">
            <label>&nbsp;</label>
            {error && <div className="alert alert-danger">{error}</div>}
            {success && <div className="alert alert-success">Deposit reward voucher awarded.</div>}
            <button
              className="btn btn-primary btn-block"
              disabled={disabled}
              onClick={handleSubmit}
            >
              {t("button.award")}
            </button>
          </div>
        </div>
      </div>
    </Collapsible>
  );
}
